import { useI18n } from 'vue-i18n';
import { RouteLocationRaw, useRouter } from 'vue-router';

import { storeToRefs } from 'pinia';

import { deleteVoice, nextChatId } from '@/api/apiService';
import { useMic } from '@/composables/shared/useMic';
import { useAlertStore } from '@/stores/alert';
import { useCallStore } from '@/stores/call';
import { QRCodeType } from '@/stores/chat';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast';
import { UserType, useUserStore } from '@/stores/user';
import { IAlertMessage } from '@/types/components';
import { isInCapacitorApp } from '@/utils/device';
import { isQrAccessType } from '@/utils/enums';
import redirectToLoginPage from '@/utils/redirectToLoginPage';

let instance = null as ReturnType<typeof useNotification> | null;

export function useNotification() {
  if (!instance) instance = createNotificationInstance();

  return instance;
}

function createNotificationInstance() {
  const router = useRouter();
  const alertStore = useAlertStore();
  const callStore = useCallStore();
  const socketStore = useSocketStore();
  const userStore = useUserStore();
  const toastStore = useToastStore();

  const { userInfo } = storeToRefs(userStore);
  const { selectedChatId } = storeToRefs(socketStore);

  const { t } = useI18n();
  const { micClear } = useMic();

  const isMobile = isInCapacitorApp();

  // 사용량 초과 시
  const alertExceededUsageLimit = (callbackConfirm?: () => void) => {
    alertStore.alert(
      {
        title: t('guide'),
        message: t('no_time_for_call'),
        subMessage: t('no_time_for_call_submessage'),
      },
      {
        confirm: () => {
          if (callbackConfirm) callbackConfirm();
          micClear('streaming');
        },
      }
    );
  };

  // 강퇴 혹은 중복 접속 시
  const alertEvictMyself = (callbackConfirm: () => void) => {
    alertStore.confirm(
      {
        title: t('alert_title_dupl_access'),
        message: t('alert_message_dupl_access'),
      },
      {
        confirm: async () => {
          socketStore.evictMySelf = '1';
          callbackConfirm();
        },
        cancel: () => {},
      },
      true
    );
  };

  // 호스트 부재 시
  const alertNoEmployee = (callbackConfirm?: () => void) => {
    const subMessage = !isQrAccessType.isNormal(callStore.qrCodeInfo.accessType)
      ? t('alert_submessage_prevent_access')
      : null;
    alertStore.alert(
      {
        title: t('alert_title_prevent_access'),
        message: t('alert_message_prevent_access'),
        subMessage: subMessage,
      },
      {
        confirm: () => {
          if (callbackConfirm) callbackConfirm();
        },
      },
      false
    );
  };

  // 이미 종료된 통역에 재접속 안내
  const alertAlreadyEndedCallWithReconnect = (callbackConfirm: () => void) => {
    alertStore.confirm(
      {
        title: t('guide'),
        message: t('already_call_ended_reconnect'),
      },
      {
        confirm: async () => {
          callbackConfirm();
        },
      }
    );
  };

  // 통역 사용량 초과
  const alertExceeded = () => {
    alertStore.alert(
      {
        title: t('guide'),
        message: t('no_time_for_call'),
        subMessage: t('no_time_for_call_submessage'),
      },
      {
        confirm: () => {
          router.push({
            name: 'chat-entrance',
            params: { code: callStore.qrCodeInfo.code },
          });
        },
      }
    );
  };

  // 통역 종료 시
  const alertCallEnded = (to: RouteLocationRaw) => {
    alertStore.alert(
      {
        title: t('alert_title_exit'),
        message: t('alert_message_end_call'),
      },
      {
        confirm: async () => {
          socketStore.evicted = false;
          await router.push(to);
        },
      },
      false
    );
  };

  // 통역 종료 시
  const alertCallEndedToLogin = () => {
    alertStore.alert(
      {
        title: t('alert_title_exit'),
        message: t('alert_message_end_call'),
      },
      {
        confirm: async () => {
          socketStore.evicted = false;
          userStore.requestLogout();
          redirectToLoginPage();
        },
      },
      false
    );
  };

  // 로그아웃 클릭 시
  const alertLogout = async () => {
    alertStore.confirm(
      {
        title: t('logout'),
        message: t('alert_body_title_login_access'),
      },
      {
        confirm: async () => {
          await userStore.requestLogout();
          await userStore.fetchUserInfo();
          if (QRCodeType.isEmployee(callStore.qrCodeInfo.qrCodeType) && UserType.isUser(userInfo.value.userType)) {
            const afterLoginPath = `${location.pathname}&partnerCode=${callStore.qrCodeInfo.partnerCode}`;
            redirectToLoginPage(afterLoginPath);
          }
        },
      },
      true
    );
  };

  // 통역 코드가 유효하지 않을 경우
  const alertInvalidCode = async () => {
    alertStore.alert(
      {
        title: t('guide'),
        message: t('error_no_call_found'),
      },
      {
        confirm: () => {
          window.location.href = 'https://www.sconchat.com/';
        },
        selfToClose: false,
      }
    );
  };

  const alertAlreadyCalling = (closeWindow = false) => {
    const isPartner = UserType.isPartner(userStore.userInfo.userType);
    const message = isPartner ? t('already_call_another_host') : t('already_calling');
    alertStore.alert(
      {
        title: t('guide'),
        message: message,
      },
      {
        confirm: () => {
          if (closeWindow) {
            window.close();
          }
        },
      },
      false
    );
  };

  // 이미 종료된 통역일 경우
  const alertAlreadyEndedCall = (closeWindow = false) => {
    alertStore.alert(
      {
        title: t('alert_title_prevent_access'),
        message: t('already_call_ended'),
      },
      {
        confirm: () => {
          if (closeWindow) {
            window.close();
          }
        },
      },
      false
    );
  };

  // 네트워크 오류
  const alertNetworkError = (confirmAction = () => {}) => {
    alertStore.alert(
      {
        title: t('guide'),
        message: t('error_server_or_network'),
        subMessage: t('alert_message_unknown_error_end'),
      },
      { confirm: confirmAction },
      true
    );
  };

  // 참여자 수 초과
  const alertExceededNumOfMembers = () => {
    alertStore.alert(
      {
        title: t('alert_title_exceeded_number_of_calling_users'),
        message: t('alert_message_exceeded_number_of_calling_users'),
      },
      {
        confirm: () => {},
      },
      false
    );
  };

  // 호스트가 게스트로 로그인 시 안내
  const alertLoginGuestAccess = (callbackConfirm: () => void) => {
    alertStore.confirm(
      {
        title: t('alert_title_login_access'),
        bodyTitle: t('alert_body_title_login_access'),
        message: t('alert_message_login_access'),
      },
      {
        confirm: async () => {
          await userStore.requestLogout();
          await userStore.fetchUserInfo();
          callbackConfirm();
        },
      },
      true
    );
  };

  // 게스트 강퇴 확인
  const alertConfirmEvictGuest = (attendee) => {
    alertStore.confirm(
      {
        title: t('alert_kick_out'),
        message: `${t('alert_is_kick_out_really_front')}${attendee.nickname}${t('alert_is_kick_out_really_end')}`,
      },
      {
        confirm: async () => {
          socketStore.sendMessageEvictUser(attendee.userId, socketStore.selectedChatId);
          toastStore.info(t('toast_completed_kick_out'));
          socketStore.removeAttendeeByUserId(attendee.userId);
        },
      }
    );
  };

  const alertConfirmVoiceRegister = () => {
    alertStore.confirm(
      {
        title: t('alert_title_recognize_my_voice'),
        bodyTitle: t('alert_body_title_recognize_my_voice'),
        message: t('alert_message_recognize_my_voice'),
        subMessage: t('alert_submessage_recognize_my_voice'),
      },
      {
        confirm: async () => {
          router.push({
            name: 'chat-entrance',
            params: {
              code: callStore.qrCodeInfo.code,
            },
            query: { showVoiceRegister: true },
          });
        },
      },
      true
    );
  };

  const alertAccessSimultaneous = () => {
    alertStore.alert(
      {
        title: t('alert_title_sametime_access'),
        message: t('alert_message_sametime_access'),
      },
      {
        confirm: () => {
          redirectToLoginPage();
        },
        selfToClose: false,
      }
    );
  };

  const alertEvicted = () => {
    alertStore.alert(
      {
        title: t('alert_title_exit'),
        message: t('alert_message_exit'),
      },
      {
        confirm: async () => {
          await router.push({
            name: 'chat-entrance',
            params: {
              code: callStore.qrCodeInfo.guestCode,
            },
          });
        },
        selfToClose: false,
      }
    );
  };

  const alertUnauthorized = () => {
    alertStore.alert(
      {
        title: t('access_denied_title'),
        message: t('access_denied_message'),
      },
      {
        confirm: () => {
          userStore.requestLogout();
          redirectToLoginPage();
        },
      },
      false
    );
  };

  const alertDeleteVoice = async (id: string, callbackConfirm: () => void) => {
    alertStore.confirm(
      {
        title: t('delete_voice'),
        message: t('delete_voice_message'),
      },
      {
        confirm: async () => {
          const { success } = await deleteVoice(id);

          if (success) {
            toastStore.info(t('deleted'));
            callbackConfirm();
          }
        },
      },
      true,
      t('confirm')
    );
  };

  const alertEndCall = (callbackConfirm: () => void) => {
    const callTitle = callStore.qrCodeInfo?.title || '';
    alertStore.confirm(
      {
        title: t('end_of_call'),
        message: t('end_of_call_message'),
        bodyAccentTitle: callTitle,
      },
      {
        confirm: async () => {
          callbackConfirm();
        },
      },
      true,
      t('end_of_call_button')
    );
  };

  const alertGoToCallEntrance = () => {
    alertStore.confirm(
      {
        title: t('alert_message_end_call'),
        message: t('confirm_go_to_entrance'),
      },
      {
        confirm: async () => {
          const codeCall = callStore.qrCodeInfo.code ?? callStore.qrCodeInfo.code.guestCode;
          const { success, data } = await nextChatId(codeCall);
          if (success) {
            localStorage.setItem('nextChatIdTimestamp', new Date().getTime());
            router.push({
              name: 'chat-entrance',
              params: { code: codeCall },
            });
          }
        },
      }
    );
  };

  const alertLogoutWhileInChat = (callbackConfirm: () => void, callbackCancel: () => void) => {
    alertStore.confirm(
      {
        title: t('alert_body_title_login_access'),
        message: t('alert_logout_while_in_chat'),
      },
      {
        confirm: callbackConfirm,
        cancel: callbackCancel,
      },
      true,
      t('logout')
    );
  };

  const alertPauseContract = (callbackConfirm: () => void) => {
    alertStore.confirm(
      {
        title: t('guide'),
        message: t('alert_confirm_stop_usage'),
      },
      {
        confirm: async () => {
          callbackConfirm();
        },
      }
    );
  };

  const alertRestartContract = (callbackConfirm: () => void) => {
    alertStore.confirm(
      {
        title: t('guide'),
        message: t('alert_confirm_restart_usage'),
      },
      {
        confirm: async () => {
          callbackConfirm();
        },
      }
    );
  };

  const alertDeleteContract = (callbackConfirm: () => void) => {
    alertStore.confirm(
      {
        title: t('guide'),
        message: t('confirm_delete_contract'),
      },
      {
        confirm: async () => {
          callbackConfirm();
        },
      }
    );
  };

  const alertAutoRenewDeactivate = (callbackConfirm: () => void) => {
    alertStore.confirm(
      {
        title: t('guide'),
        message: t('auto_renew_prompt_off'),
      },
      {
        confirm: async () => {
          callbackConfirm();
        },
      }
    );
  };

  const alertCancelFromDowngrade = (message, callbackConfirm: () => void) => {
    alertStore.confirm(
      {
        title: t('confirm_cancel_subscription'),
        message,
      },
      {
        confirm: async () => {
          callbackConfirm();
        },
      }
    );
  };

  const alertCancelPlanned = ({ subscriptionPlanned, subscriptionActive, callbackConfirm }) => {
    alertStore.confirm(
      {
        title: t('ss_cancel_planned'),
        message: t('ss_cancel_planned_message', {
          subscriptionPlanned,
          subscriptionActive,
        }),
      },
      {
        confirm: () => {
          callbackConfirm();
        },
      }
    );
  };

  // 세션 종료
  const alertSessionEnded = () => {
    alertStore.alert(
      {
        title: t('alert_session_end_title'),
        message: `${t('alert_end_chat_by_host')}<br> ${t('alert_session_end_message')}`,
      },
      {
        confirm: () => {
          router.push({
            name: 'chat-entrance',
            params: { code: callStore.qrCodeInfo.code },
          });
        },
      }
    );
  };

  // 세션 종료
  const alertSessionEndedSingle = () => {
    alertStore.alert(
      {
        title: t('alert_session_end_title'),
        message: t('alert_session_end_message'),
      },
      {
        confirm: () => {
          userStore.requestLogout();
          redirectToLoginPage();
        },
      }
    );
  };

  const confirmBasic = (title, message, callbackConfirm) => {
    alertStore.confirm(
      {
        title,
        message,
      },
      {
        confirm: async () => {
          callbackConfirm();
        },
      }
    );
  };

  const alertBasic = (textBundle: IAlertMessage, callbackConfirm: () => void = () => {}) => {
    alertStore.alert(textBundle, {
      confirm: callbackConfirm,
    });
  };

  return {
    confirmBasic,
    alertBasic,
    alertLogout,
    alertEndCall,
    alertEvicted,
    alertExceeded,
    alertCallEnded,
    alertNoEmployee,
    alertEvictMyself,
    alertInvalidCode,
    alertDeleteVoice,
    alertNetworkError,
    alertUnauthorized,
    alertSessionEnded,
    alertPauseContract,
    alertCancelPlanned,
    alertAlreadyCalling,
    alertDeleteContract,
    alertRestartContract,
    alertAlreadyEndedCall,
    alertCallEndedToLogin,
    alertGoToCallEntrance,
    alertLogoutWhileInChat,
    alertLoginGuestAccess,
    alertConfirmEvictGuest,
    alertSessionEndedSingle,
    alertExceededUsageLimit,
    alertAccessSimultaneous,
    alertAutoRenewDeactivate,
    alertCancelFromDowngrade,
    alertConfirmVoiceRegister,
    alertExceededNumOfMembers,
    alertAlreadyEndedCallWithReconnect,
  };
}
